import type { ElementType, Ref } from "react";
import type { ResponsiveValue } from "styled-system";
import { variant } from "styled-system";
import { css, cx } from "@emotion/css";
import { createComponent, createHook } from "reakit-system";
import type { PropsWithAs } from "../as";
import { forwardRefWithAs } from "../as";
import type { TextHTMLProps, TextOptions } from "../Text/Text";
import { useText } from "../Text/Text";

type Level = ResponsiveValue<1 | 2 | 3 | 4 | 5 | 6 | "1" | "2" | "3" | "4" | "5" | "6">;

type HeadingVariants = {
  [variant: number]: {
    [key: string]: string[];
  };
};

function generateHeadingsVariants(): HeadingVariants {
  let headings: HeadingVariants = {};

  for (let i = 1; i <= 6; i++) {
    headings[i] = {
      fontFamily: ["headings"],
      fontSize: [`h${i}`, `h${i}Big`],
      lineHeight: [`h${i}LineHeight`, `h${i}BigLineHeight`],
      fontWeight: [`h${i}FontWeight`],
    };
  }

  return headings;
}

const levelVariants = variant({
  prop: "level",
  variants: {
    ...generateHeadingsVariants(),
  },
});
/**
 * @beta
 */
export type HeadingOptions = { level: Level } & Omit<
  TextOptions,
  "fontWeight" | "lineHeight" | "fontSize"
>;

export type HeadingHTMLProps = TextHTMLProps;

export type HeadingProps = HeadingHTMLProps & HeadingOptions;

export const useHeading = createHook<HeadingOptions, HeadingHTMLProps>({
  name: "Heading",
  keys: ["level"],
  compose: [useText],
  useOptions(options) {
    return {
      ...options,
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(
        props.className,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- не можем влиять на типы из styled-system
        css({ label: "Heading" }, levelVariants(options))
      ),
    };
  },
});

const HeadingComponent = createComponent({
  as: "h1",
  useHook: useHeading,
  memo: true,
});

/**
 * @beta
 */
export const Heading = forwardRefWithAs<HeadingProps, "h1">(function (
  { as, level = 1, ...props }: PropsWithAs<HeadingProps>,
  ref: Ref<HTMLHeadingElement>
) {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- мы знаем, что там будет либо массив, либо одно значение
  const Component = as || `h${Array.isArray(level) ? level[0] : level}`;
  return <HeadingComponent as={Component as ElementType} level={level} ref={ref} {...props} />;
});
